import React, { useState } from 'react';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Calendar,
  Clock,
  Heart,
  Activity,
  Pill,
  Syringe,
  Thermometer,
  Weight,
  Ruler,
  AlertCircle,
  CheckCircle,
  Download,
  Share2,
  Edit3,
  Eye,
  Trash2,
  X,
  Upload,
  Camera,
  User,
  Phone,
  MapPin,
  Star,
  Award,
  TrendingUp,
  TrendingDown,
  Minus,
  Info,
  Bell,
  Shield,
  Zap,
  BookOpen,
  Archive,
  Settings,
  ChevronRight,
  ChevronDown,
  PawPrint
} from 'lucide-react';

const LivestockManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPet, setSelectedPet] = useState(null);
  const [showAddRecordModal, setShowAddRecordModal] = useState(false);
  const [showPetModal, setShowPetModal] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [newRecord, setNewRecord] = useState({
    type: 'vaccination',
    title: '',
    date: '',
    veterinarian: '',
    clinic: '',
    description: '',
    status: 'completed'
  });

  const pets = [
    {
      id: 1,
      name: 'Max',
      species: 'Dog',
      breed: 'Golden Retriever',
      age: '3 years',
      weight: '32 kg',
      gender: 'Male',
      microchip: 'MC12345678',
      owner: 'John Doe',
      image: '/api/placeholder/80/80',
      lastVisit: '2024-01-15',
      nextAppointment: '2024-02-20',
      status: 'healthy',
      vaccinations: 'up-to-date',
      medications: 2
    },
    {
      id: 2,
      name: 'Luna',
      species: 'Cat',
      breed: 'Persian',
      age: '2 years',
      weight: '4.5 kg',
      gender: 'Female',
      microchip: 'MC87654321',
      owner: 'Jane Smith',
      image: '/api/placeholder/80/80',
      lastVisit: '2024-01-20',
      nextAppointment: '2024-03-15',
      status: 'treatment',
      vaccinations: 'due',
      medications: 1
    },
    {
      id: 3,
      name: 'Buddy',
      species: 'Dog',
      breed: 'Labrador',
      age: '5 years',
      weight: '28 kg',
      gender: 'Male',
      microchip: 'MC11223344',
      owner: 'Mike Johnson',
      image: '/api/placeholder/80/80',
      lastVisit: '2023-12-10',
      nextAppointment: '2024-02-25',
      status: 'healthy',
      vaccinations: 'up-to-date',
      medications: 0
    }
  ];

  const healthRecords = [
    {
      id: 1,
      petId: 1,
      type: 'vaccination',
      title: 'Annual Vaccination',
      date: '2024-01-15',
      veterinarian: 'Dr. Sarah Wilson',
      clinic: 'Happy Paws Veterinary',
      description: 'Complete annual vaccination including rabies, DHPP, and bordetella',
      attachments: ['vaccination_record.pdf'],
      status: 'completed',
      nextDue: '2025-01-15'
    },
    {
      id: 2,
      petId: 1,
      type: 'checkup',
      title: 'Regular Health Checkup',
      date: '2024-01-15',
      veterinarian: 'Dr. Sarah Wilson',
      clinic: 'Happy Paws Veterinary',
      description: 'Routine health examination with blood work and physical assessment',
      vitals: {
        temperature: '38.5°C',
        heartRate: '85 bpm',
        weight: '32 kg',
        bloodPressure: 'Normal'
      },
      attachments: ['blood_work.pdf', 'xray_chest.jpg'],
      status: 'completed',
      findings: 'Healthy, no concerns'
    },
    {
      id: 3,
      petId: 2,
      type: 'medication',
      title: 'Antibiotics Treatment',
      date: '2024-01-20',
      veterinarian: 'Dr. Mark Thompson',
      clinic: 'City Animal Hospital',
      description: 'Prescribed antibiotics for ear infection',
      medication: {
        name: 'Amoxicillin',
        dosage: '250mg',
        frequency: 'Twice daily',
        duration: '10 days'
      },
      attachments: ['prescription.pdf'],
      status: 'ongoing',
      nextDue: '2024-01-30'
    },
    {
      id: 4,
      petId: 1,
      type: 'surgery',
      title: 'Dental Cleaning',
      date: '2023-11-28',
      veterinarian: 'Dr. Lisa Chen',
      clinic: 'Dental Care Veterinary',
      description: 'Professional dental cleaning and minor tooth extraction',
      attachments: ['dental_report.pdf', 'before_after.jpg'],
      status: 'completed',
      findings: 'Dental health improved significantly'
    }
  ];

  const recordTypes = [
    { id: 'all', name: 'All Records', icon: FileText },
    { id: 'vaccination', name: 'Vaccinations', icon: Syringe },
    { id: 'checkup', name: 'Checkups', icon: Heart },
    { id: 'medication', name: 'Medications', icon: Pill },
    { id: 'surgery', name: 'Surgery', icon: Activity },
    { id: 'emergency', name: 'Emergency', icon: AlertCircle }
  ];

  const upcomingReminders = [
    {
      id: 1,
      petName: 'Max',
      type: 'vaccination',
      title: 'Rabies Vaccination Due',
      date: '2024-02-20',
      priority: 'high'
    },
    {
      id: 2,
      petName: 'Luna',
      type: 'medication',
      title: 'Medication Refill',
      date: '2024-02-15',
      priority: 'medium'
    },
    {
      id: 3,
      petName: 'Buddy',
      type: 'checkup',
      title: 'Annual Health Checkup',
      date: '2024-02-25',
      priority: 'low'
    }
  ];

  const filteredRecords = healthRecords.filter(record => {
    const matchesSearch = record.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.veterinarian.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || record.type === filterType;
    const matchesPet = !selectedPet || record.petId === selectedPet.id;
    return matchesSearch && matchesFilter && matchesPet;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-green-100 text-green-800';
      case 'treatment': return 'bg-yellow-100 text-yellow-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRecordIcon = (type) => {
    switch (type) {
      case 'vaccination': return Syringe;
      case 'checkup': return Heart;
      case 'medication': return Pill;
      case 'surgery': return Activity;
      case 'emergency': return AlertCircle;
      default: return FileText;
    }
  };

  const handleAddRecord = () => {
    setShowAddRecordModal(true);
  };

  const handleViewPet = (pet) => {
    setSelectedPet(pet);
    setShowPetModal(true);
  };

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setShowRecordModal(true);
  };

  const handleSaveRecord = () => {
    // In a real app, you would save to database here
    setShowAddRecordModal(false);
    setNewRecord({
      type: 'vaccination',
      title: '',
      date: '',
      veterinarian: '',
      clinic: '',
      description: '',
      status: 'completed'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRecord(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Pet Health Records</h1>
                <p className="text-sm text-gray-500">Comprehensive digital health records for your pets</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-emerald-100 px-3 py-1 rounded-full">
                <Shield className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-700 text-sm font-medium">Secure</span>
              </div>
              <div className="flex items-center space-x-2 bg-blue-100 px-3 py-1 rounded-full">
                <Zap className="w-4 h-4 text-blue-600" />
                <span className="text-blue-700 text-sm font-medium">Cloud Sync</span>
              </div>
              <button
                onClick={handleAddRecord}
                className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add Record</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'overview'
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-700 hover:text-emerald-600'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('records')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'records'
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-700 hover:text-emerald-600'
              }`}
            >
              Health Records
            </button>
            <button
              onClick={() => setActiveTab('reminders')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'reminders'
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-700 hover:text-emerald-600'
              }`}
            >
              Reminders
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'analytics'
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-700 hover:text-emerald-600'
              }`}
            >
              Analytics
            </button>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Pet Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pets.map(pet => (
                <div
                  key={pet.id}
                  className={`bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                    selectedPet?.id === pet.id ? 'ring-2 ring-emerald-600' : ''
                  }`}
                  onClick={() => setSelectedPet(pet)}
                >
                  <div className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full flex items-center justify-center">
                        <PawPrint className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{pet.name}</h3>
                        <p className="text-sm text-gray-600">{pet.breed} • {pet.dateOfBirth}</p>
                        <p className="text-sm text-gray-500">{pet.gender} • {pet.weight}</p>
                      </div>
                    </div>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(pet.status)}`}>
                          {pet.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Vaccinations</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          pet.vaccinations === 'up-to-date' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {pet.vaccinations}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Active Medications</span>
                        <span className="text-sm font-medium">{pet.medications}</span>
                      </div>
                    </div>
                    <div className="border-t pt-4 flex items-center justify-between text-sm text-gray-600">
                      <div>
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>Last visit: {pet.lastVisit}</span>
                        </div>
                        <div className="flex items-center space-x-1 mt-1">
                          <Clock className="w-4 h-4" />
                          <span>Next: {pet.nextAppointment}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewPet(pet);
                        }}
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">24</p>
                    <p className="text-sm text-gray-600">Total Records</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Syringe className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">8</p>
                    <p className="text-sm text-gray-600">Vaccinations</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <Bell className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">3</p>
                    <p className="text-sm text-gray-600">Reminders</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">2</p>
                    <p className="text-sm text-gray-600">Ongoing Treatments</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Health Records Tab */}
        {activeTab === 'records' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white p-6 rounded-2xl shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search records..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  {recordTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <select
                  value={selectedPet?.id || ''}
                  onChange={(e) => setSelectedPet(pets.find(p => p.id === parseInt(e.target.value)) || null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">All Pets</option>
                  {pets.map(pet => (
                    <option key={pet.id} value={pet.id}>
                      {pet.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Records List */}
            <div className="space-y-4">
              {filteredRecords.map(record => {
                const RecordIcon = getRecordIcon(record.type);
                return (
                  <div key={record.id} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                    <div className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center">
                            <RecordIcon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{record.title}</h3>
                            <p className="text-sm text-gray-600">{record.description}</p>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-gray-500 mt-2">
                              <div className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4" />
                                <span>{record.date}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <User className="w-4 h-4" />
                                <span>{record.veterinarian}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <MapPin className="w-4 h-4" />
                                <span>{record.clinic}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                            record.status === 'completed' ? 'bg-green-100 text-green-800' : 
                            record.status === 'ongoing' ? 'bg-blue-100 text-blue-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {record.status}
                          </div>
                          <button
                            onClick={() => handleViewRecord(record)}
                            className="text-emerald-600 hover:text-emerald-700"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600">
                            <Download className="w-4 h-4" />
                          </button>
                          <button className="text-gray-400 hover:text-gray-600">
                            <Share2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Reminders Tab */}
        {activeTab === 'reminders' && (
          <div className="space-y-6">
            {upcomingReminders.map(reminder => (
              <div key={reminder.id} className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                  <div className="flex items-start sm:items-center space-x-4 mb-4 sm:mb-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      reminder.priority === 'high' ? 'bg-red-100' :
                      reminder.priority === 'medium' ? 'bg-yellow-100' : 'bg-green-100'
                    }`}>
                      <Bell className={`w-6 h-6 ${
                        reminder.priority === 'high' ? 'text-red-600' :
                        reminder.priority === 'medium' ? 'text-yellow-600' : 'text-green-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{reminder.title}</h3>
                      <p className="text-sm text-gray-600">Pet: {reminder.petName}</p>
                      <div className="flex items-center space-x-1 text-sm text-gray-500 mt-1">
                        <Calendar className="w-4 h-4" />
                        <span>{reminder.date}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium self-start sm:self-auto ${
                      reminder.priority === 'high' ? 'bg-red-100 text-red-800' :
                      reminder.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {reminder.priority} priority
                    </div>
                    <button className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors">
                      Set Reminder
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Health Analytics</h3>
              <p className="text-gray-600">Comprehensive health trends and insights coming soon</p>
            </div>
          </div>
        )}
      </div>

      {/* Add Record Modal */}
      {showAddRecordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Add Health Record</h3>
                <button
                  onClick={() => setShowAddRecordModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Record Type</label>
                  <select 
                    name="type"
                    value={newRecord.type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="vaccination">Vaccination</option>
                    <option value="checkup">Checkup</option>
                    <option value="medication">Medication</option>
                    <option value="surgery">Surgery</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={newRecord.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter record title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                  <input
                    type="date"
                    name="date"
                    value={newRecord.date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Veterinarian</label>
                  <input
                    type="text"
                    name="veterinarian"
                    value={newRecord.veterinarian}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter veterinarian name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Clinic</label>
                  <input
                    type="text"
                    name="clinic"
                    value={newRecord.clinic}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Enter clinic name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={newRecord.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    rows="3"
                    placeholder="Enter record description"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select 
                    name="status"
                    value={newRecord.status}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="completed">Completed</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="scheduled">Scheduled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
                  <div className="flex items-center justify-center w-full">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-8 h-8 mb-3 text-gray-400" />
                        <p className="mb-2 text-sm text-gray-500">Click to upload or drag and drop</p>
                        <p className="text-xs text-gray-500">PDF, JPG, PNG (MAX. 10MB each)</p>
                      </div>
                      <input type="file" className="hidden" />
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex space-x-3 pt-6">
                <button
                  onClick={() => setShowAddRecordModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveRecord}
                  className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700"
                >
                  Save Record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Pet Modal */}
      {showPetModal && selectedPet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">{selectedPet.name}'s Profile</h3>
                <button
                  onClick={() => setShowPetModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-full flex items-center justify-center">
                  <PawPrint className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{selectedPet.name}</h3>
                  <p className="text-sm text-gray-600">{selectedPet.breed} • {selectedPet.dateOfBirth}</p>
                  <div className={`mt-1 px-2 py-1 rounded-full text-xs inline-block ${getStatusColor(selectedPet.status)}`}>
                    {selectedPet.status}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Species</p>
                  <p className="font-medium">{selectedPet.species}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Gender</p>
                  <p className="font-medium">{selectedPet.gender}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Weight</p>
                  <p className="font-medium">{selectedPet.weight}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Microchip</p>
                  <p className="font-medium">{selectedPet.microchip}</p>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">Vaccinations</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    selectedPet.vaccinations === 'up-to-date' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedPet.vaccinations}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">Active Medications</p>
                  <span className="font-medium">{selectedPet.medications}</span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">Last Visit</p>
                  <span>{selectedPet.lastVisit}</span>
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">Next Appointment</p>
                  <span className="font-medium text-emerald-600">{selectedPet.nextAppointment}</span>
                </div>
              </div>
              <div className="mt-6">
                <button className="w-full bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700">
                  View Full Health History
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Record Modal */}
      {showRecordModal && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedRecord.title}</h3>
                  <p className="text-sm text-gray-600">
                    {pets.find(p => p.id === selectedRecord.petId)?.name} • 
                    {selectedRecord.date} • {selectedRecord.clinic}
                  </p>
                </div>
                <button
                  onClick={() => setShowRecordModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-lg flex items-center justify-center">
                  {React.createElement(getRecordIcon(selectedRecord.type), {
                    className: "w-6 h-6 text-white"
                  })}
                </div>
                <div className="flex-1">
                  <p className="text-gray-700">{selectedRecord.description}</p>
                  <div className="mt-3 flex items-center space-x-2">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      selectedRecord.status === 'completed' ? 'bg-green-100 text-green-800' : 
                      selectedRecord.status === 'ongoing' ? 'bg-blue-100 text-blue-800' : 
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedRecord.status}
                    </div>
                    <div className="text-sm text-gray-600">
                      <User className="inline w-4 h-4 mr-1" />
                      {selectedRecord.veterinarian}
                    </div>
                  </div>
                </div>
              </div>
              
              {selectedRecord.vitals && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Vital Signs</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-600">Temperature</p>
                      <p className="font-medium">{selectedRecord.vitals.temperature}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-600">Heart Rate</p>
                      <p className="font-medium">{selectedRecord.vitals.heartRate}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-600">Weight</p>
                      <p className="font-medium">{selectedRecord.vitals.weight}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-sm text-gray-600">Blood Pressure</p>
                      <p className="font-medium">{selectedRecord.vitals.bloodPressure}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedRecord.medication && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Medication Details</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Medication</p>
                        <p className="font-medium">{selectedRecord.medication.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Dosage</p>
                        <p className="font-medium">{selectedRecord.medication.dosage}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Frequency</p>
                        <p className="font-medium">{selectedRecord.medication.frequency}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Duration</p>
                        <p className="font-medium">{selectedRecord.medication.duration}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {selectedRecord.findings && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Findings</h4>
                  <div className="bg-emerald-50 p-4 rounded-lg">
                    <p className="text-emerald-800">{selectedRecord.findings}</p>
                  </div>
                </div>
              )}
              
              {selectedRecord.nextDue && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Next Due Date</h4>
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-emerald-600 mr-2" />
                    <span className="font-medium">{selectedRecord.nextDue}</span>
                  </div>
                </div>
              )}
              
              {selectedRecord.attachments && selectedRecord.attachments.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Attachments</h4>
                  <div className="space-y-2">
                    {selectedRecord.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                        <div className="flex items-center">
                          <FileText className="w-5 h-5 text-gray-500 mr-2" />
                          <span className="text-sm">{file}</span>
                        </div>
                        <button className="text-emerald-600 hover:text-emerald-700">
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex space-x-3 pt-4">
                <button className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50">
                  Edit Record
                </button>
                <button className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700">
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LivestockManagement;